import { useEffect, useRef, useState } from 'react';

import { haptic } from '../telegram';

/** One row of a wheel, and the rhythm every measurement here is a multiple of.
    The two rows of air above and below the list - `.wheel-pad` in the stylesheet
    - are what let the first and the last value reach the middle. */
const ROW = 34;

/**
 * The column every wheel in the app is built from.
 *
 * It lived inside `BirthdayWheel` until the schedule grew a wheel of its own -
 * the date of a gathering, in three columns - and the two would otherwise have
 * held two copies of the same scroller. What is shared is the whole of the
 * behaviour: the snap points, the ladder of opacities, the tap on a neighbour,
 * `scrollend` with a timer standing in for it, and the tap of the motor. What
 * is not shared is where the values come from, which is the caller's business.
 *
 * The value is the row nearest the middle, and it is read off `scrollTop`
 * rather than tracked by hand - the browser owns the momentum, and anything
 * that tries to own it too fights the finger.
 */
export function Column({
  label,
  items,
  index,
  onPick,
  wide = false,
  mono = false,
  quiet = false,
}: {
  label: string;
  items: string[];
  /** 0-based row this column stands on. */
  index: number;
  onPick: (index: number) => void;
  /** The month, which needs the room «сентябрь» takes. */
  wide?: boolean;
  /** Figures, which are set in the monospaced face like every other figure. */
  mono?: boolean;
  quiet?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /**
   * Which row the ladder of opacities is drawn around. It follows the finger
   * during the scroll, while `index` only changes once the wheel has settled -
   * so the two disagree for as long as a flick lasts, and that is the point.
   */
  const [live, setLive] = useState(index);
  // Read inside a listener that is attached once: state would be the value it
  // had when the listener was made.
  const liveRef = useRef(index);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  /**
   * Whether the wheel has been put where it belongs once already.
   *
   * The run below is the same effect on the first pass, and on that pass there
   * is nothing to travel from: a wheel gliding up from its first row on every
   * mount is an entrance nobody's finger asked for.
   */
  const placed = useRef(false);

  // Somebody else moved the wheel - a month change clamping the day, or the
  // server answering with something other than what was sent.
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    liveRef.current = index;
    setLive(index);
    const top = index * ROW;
    if (Math.abs(el.scrollTop - top) > 1) {
      /* Smoothly, which is what a tap on a neighbouring row has always got.
         The value moved for a reason the person caused - they changed the month
         and it clamped the day - so the wheel owes them the same account of it,
         and a figure that is different between two frames is a figure they have
         to read twice. A stylesheet cannot shorten a scroll, so the query is
         read here the way `Unfold` reads it. */
      const still =
        !placed.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollTo(still ? { top } : { top, behavior: 'smooth' });
    }
    placed.current = true;
  }, [index]);

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;

    const nearest = (): number =>
      Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / ROW)));

    function settle(): void {
      onPickRef.current(nearest());
    }

    let timer: number | undefined;
    function onScroll(): void {
      const next = nearest();
      if (next !== liveRef.current) {
        liveRef.current = next;
        setLive(next);
        // The same tap of the motor the digits of the code get: a value
        // changed, and on a wheel that is the only event there is.
        haptic('selection');
      }
      // `scrollend` is the right event and is younger than the phones this app
      // runs on; where it is missing, quiet for a moment means settled.
      if (!SCROLLEND) {
        window.clearTimeout(timer);
        timer = window.setTimeout(settle, 140);
      }
    }

    el.addEventListener('scroll', onScroll, { passive: true });
    if (SCROLLEND) el.addEventListener('scrollend', settle);
    return () => {
      window.clearTimeout(timer);
      el.removeEventListener('scroll', onScroll);
      if (SCROLLEND) el.removeEventListener('scrollend', settle);
    };
  }, [items.length]);

  const className = ['wheel-col', wide && 'wheel-col--wide', mono && 'wheel-col--num']
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      className={quiet ? `${className} wheel-col--quiet` : className}
      role="listbox"
      aria-label={label}
      // A control that is not yours does not answer a finger, and this one
      // becomes yours the moment a month is chosen.
      aria-disabled={quiet || undefined}
    >
      <span className="wheel-pad" aria-hidden="true" />
      {items.map((item, position) => (
        <button
          key={item}
          className={`wheel-row ${ladder(position - live)}`}
          data-press="none"
          role="option"
          aria-selected={position === index}
          tabIndex={position === index ? 0 : -1}
          // A tap on a neighbour is the short way to move one row, and the
          // wheel then reports the value the same way it does after a flick.
          onClick={() => ref.current?.scrollTo({ top: position * ROW, behavior: 'smooth' })}
        >
          {item}
        </button>
      ))}
      <span className="wheel-pad" aria-hidden="true" />
    </div>
  );
}

/** The row on the band, one either side of it, and everything past that. */
function ladder(distance: number): string {
  const steps = Math.abs(distance);
  if (steps === 0) return 'wheel-row--picked';
  return steps === 1 ? 'wheel-row--near' : 'wheel-row--far';
}

/**
 * Whether this client has the event that says a scroll has finished. Read once:
 * it cannot change while the app is open, and the fallback timer is the same
 * measurement made by hand.
 */
const SCROLLEND = typeof window !== 'undefined' && 'onscrollend' in window;
